import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, FileText, LogOut, Settings2 } from "lucide-react";
import { showFeedback } from "../lib/feedback";
import { isDemoMode, supabase } from "../lib/supabase";
import { avatarInitials } from "../ui/avatar-identity";
import { appPath } from "./routes";

/**
 * Menu da conta do protótipo Zênite (`.z-pop.cs-account-menu`): identidade,
 * espaço de trabalho, atalhos de configurações e saída.
 *
 * Popover NÃO modal — fecha por Escape e clique fora e devolve o foco ao
 * avatar. "Sair" encerra a sessão real do Supabase; no demo não há sessão e o
 * item apenas volta à tela de entrada, como o protótipo faz.
 */
export function AccountMenu({ name, email }: { name: string; email?: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [position, setPosition] = useState<CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useLayoutEffect(() => {
    if (!open) return;
    const header = triggerRef.current?.closest("header");
    if (!header) return;
    const rect = header.getBoundingClientRect();
    setPosition({ top: rect.bottom + 3, right: Math.max(16, window.innerWidth - rect.right + 12) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const signOut = async () => {
    if (isDemoMode || !supabase) {
      go("/login");
      return;
    }
    setSigningOut(true);
    const { error } = await supabase.auth.signOut();
    setSigningOut(false);
    if (error) {
      showFeedback(`Não foi possível sair: ${error.message}`, "error");
      return;
    }
    go("/login");
  };

  const initials = avatarInitials(name);

  return (
    <div ref={rootRef} className="cx-profile">
      <button
        ref={triggerRef}
        type="button"
        className="avatar"
        aria-label={`Conta de ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        {initials}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className="z-pop cs-account-menu"
          aria-label="Menu do perfil"
          style={position}
        >
          <div className="cs-menu-identity">
            <span className="cs-avatar" aria-hidden="true">
              {initials}
            </span>
            <div>
              <strong>{name}</strong>
              {email && <span>{email}</span>}
            </div>
          </div>
          <div className="cs-menu-workspace">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="7" width="18" height="14" rx="2" />
              <path d="M8 7V3h8v4M3 12a22 22 0 0 0 18 0M10 12h4" />
            </svg>
            <span>CosmosLeads</span>
            <small>{isDemoMode ? "Demonstração" : "Conta"}</small>
          </div>
          <div className="cs-menu-group">
            <button type="button" role="menuitem" onClick={() => go(appPath("configuracoes"))}>
              <Settings2 size={16} aria-hidden="true" />
              <span>Perfil e configurações</span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
            <button type="button" role="menuitem" onClick={() => go(appPath("modelos"))}>
              <FileText size={16} aria-hidden="true" />
              <span>Modelos de mensagem</span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="cs-menu-group cs-menu-last">
            <button
              type="button"
              role="menuitem"
              onClick={() => void signOut()}
              disabled={signingOut}
              title={isDemoMode ? "No modo demonstração não há sessão: volta à tela de entrada." : undefined}
            >
              <LogOut size={16} aria-hidden="true" />
              <span>{signingOut ? "Saindo…" : "Sair"}</span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
